import { useSyncExternalStore } from "react";
import { safeReturnTo } from "./session-ended.ts";

/**
 * Silent re-federation (spec `authentication` "Silent re-federation keeps IdP groups fresh"):
 * the session check says when a federated session's IdP groups are due for renewal
 * (`idpRefreshDue`). A visible tab with nothing unsaved signs in again through the same IdP
 * (usually without a prompt) and comes back to the same page; otherwise a banner offers it.
 * The session itself never ends for this.
 */

let due = false;
let dismissed = false;
const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) {
    listener();
  }
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** What the latest session check said (absent from older APIs: not due). */
export function markIdpRefreshDue(value: boolean | undefined): void {
  const next = value === true;
  if (next !== due) {
    due = next;
    if (!next) {
      dismissed = false;
    }
    notify();
  }
}

export const useIdpRefreshDue = () => useSyncExternalStore(subscribe, () => due);
export const useIdpRefreshDismissed = () => useSyncExternalStore(subscribe, () => dismissed);

/** "Later": the banner hides until the next page load or the next time renewal falls due. */
export function dismissIdpRefresh(): void {
  dismissed = true;
  notify();
}

/** The re-federation route, coming back to `path` (same origin only, as the server checks). */
export function refederateHref(path: string): string {
  return `/api/auth/refederate?returnTo=${encodeURIComponent(safeReturnTo(path))}`;
}

/**
 * The last automatic attempt, so a renewal that came back still due (the IdP sent no groups
 * claim, say) shows the banner instead of looping through the IdP.
 */
const ATTEMPT_KEY = "esi-refederate-at";
export const REFEDERATE_RETRY_MS = 15 * 60_000;

export function recentAttempt(now = Date.now()): boolean {
  try {
    const at = Number(window.sessionStorage.getItem(ATTEMPT_KEY));
    return Number.isFinite(at) && at > 0 && now - at < REFEDERATE_RETRY_MS;
  } catch {
    // No storage (a private window, a blocked origin): never loop, always offer the banner.
    return true;
  }
}

export function recordAttempt(now = Date.now()): void {
  try {
    window.sessionStorage.setItem(ATTEMPT_KEY, String(now));
  } catch {
    // Without storage `recentAttempt` already answers true.
  }
}

/**
 * The person's last interaction with this tab. Visibility alone is not activity: an unattended
 * tab never re-federates by itself (that would outlive the session's idle timeout), it shows the
 * banner instead.
 */
let lastActivity = 0;
export const ACTIVITY_WINDOW_MS = 15 * 60_000;
export function markActivity(now = Date.now()): void {
  lastActivity = now;
}
export const recentlyActive = (now = Date.now()) => now - lastActivity < ACTIVITY_WINDOW_MS;
if (typeof window !== "undefined") {
  for (const type of ["pointerdown", "keydown", "wheel", "touchstart"] as const) {
    window.addEventListener(type, () => markActivity(), { capture: true, passive: true });
  }
}

/** Leaves for the re-federation (a full page load); replaceable in tests. */
export const refederateNavigation = {
  go: (href: string) => window.location.assign(href),
};
